import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface GerantRow {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  blocked?: boolean;
}

export interface GerantEntrepotLink {
  id: number;
  gerant_id: number;
  entrepot_id: number;
  gerant_nom?: string;
  gerant_prenom?: string;
  gerant_email?: string;
  entrepot_libelle?: string;
  code_entrepot?: string;
}

export interface EtablissementRow {
  id: number;
  libelle: string;
  code_etablissement: string;
  raison_sociale?: string | null;
  numero_identification_fiscale?: string | null;
  pays?: string | null;
  ville?: string | null;
  code_postal?: string | null;
  adresse?: string | null;
  logo?: string | null;
  user_id?: number;
  station_id?: number;
}

export type EtablissementWriteBody = {
  libelle: string;
  code_etablissement: string;
  raison_sociale?: string | null;
  numero_identification_fiscale?: string | null;
  pays?: string | null;
  ville?: string | null;
  code_postal?: string | null;
  adresse?: string | null;
  logo?: string | null;
};

export interface EtablissementEntrepotLink {
  id: number;
  etablissement_id: number;
  entrepot_id: number;
  etablissement_libelle?: string;
  code_etablissement?: string;
  entrepot_libelle?: string;
  code_entrepot?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseParametreAssociationsService {
  /** Cache GET établissements par station back (évite re-fetch modal ↔ facture). */
  private etablissementsCache = new Map<number, { at: number; rows: EtablissementRow[] }>();
  private etablissementsInflight = new Map<number, Observable<EtablissementRow[]>>();
  private static readonly ETAB_CACHE_TTL_MS = 60_000;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  private stationUrl(path: string): string {
    const stationId = lcStationId(this.authService);
    return `${lcApiBase(this.authService)}/api/entrepots${path.replace(':station_id', String(stationId))}`;
  }

  /** Invalide le cache établissements (après create/update/delete). */
  invalidateEtablissementsCache(stationId?: number): void {
    if (stationId != null && Number(stationId) > 0) {
      this.etablissementsCache.delete(Math.floor(Number(stationId)));
      this.etablissementsInflight.delete(Math.floor(Number(stationId)));
      return;
    }
    this.etablissementsCache.clear();
    this.etablissementsInflight.clear();
  }

  listGerants(): Observable<GerantRow[]> {
    const url = this.stationUrl('/gerants/station/:station_id');
    return this.http.get<{ success: boolean; gerants?: GerantRow[] }>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.gerants)) {
          throw new Error('Réponse gérants invalide');
        }
        return res.gerants;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des gérants.'))
      )
    );
  }

  listGerantEntrepotLinks(): Observable<GerantEntrepotLink[]> {
    const url = this.stationUrl('/gerant-entrepots/station/:station_id');
    return this.http.get<{ success: boolean; links?: GerantEntrepotLink[] }>(url).pipe(
      map((res) => (res?.success && Array.isArray(res.links) ? res.links : [])),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des associations gérant.'))
      )
    );
  }

  setGerantEntrepots(gerantId: number, entrepotIds: number[]): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/gerant-entrepots/${gerantId}`;
    return this.http.put<{ success: boolean }>(url, { entrepot_ids: entrepotIds }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error('Enregistrement impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de l\'association gérant / entrepôt.'))
      )
    );
  }

  createGerant(body: {
    nom: string;
    prenom: string;
    email: string;
    password: string;
  }): Observable<GerantRow> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/gerant`;
    return this.http.post<{ success: boolean; gerant?: GerantRow }>(url, body).pipe(
      map((res) => {
        if (!res?.success || !res.gerant) {
          throw new Error('Création impossible');
        }
        return res.gerant;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la création du gérant.'))
      )
    );
  }

  updateGerant(
    id: number,
    body: { nom?: string; prenom?: string; email?: string; password?: string }
  ): Observable<GerantRow> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/gerant/${id}`;
    return this.http.put<{ success: boolean; gerant?: GerantRow }>(url, body).pipe(
      map((res) => {
        if (!res?.success || !res.gerant) {
          throw new Error('Mise à jour impossible');
        }
        return res.gerant;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour du gérant.'))
      )
    );
  }

  deleteGerant(id: number): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/gerant/${id}`;
    return this.http.delete<{ success: boolean }>(url).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error('Suppression impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la suppression du gérant.'))
      )
    );
  }

  listEtablissements(forceRefresh = false): Observable<EtablissementRow[]> {
    const stationId = lcStationId(this.authService);
    if (!forceRefresh) {
      const cached = this.etablissementsCache.get(stationId);
      if (cached && Date.now() - cached.at < LogicielCaisseParametreAssociationsService.ETAB_CACHE_TTL_MS) {
        return of(cached.rows);
      }
      const inflight = this.etablissementsInflight.get(stationId);
      if (inflight) {
        return inflight;
      }
    }

    const url = this.stationUrl('/etablissements/station/:station_id');
    const req$ = this.http.get<{ success: boolean; etablissements?: EtablissementRow[] }>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.etablissements)) {
          throw new Error('Réponse établissements invalide');
        }
        return res.etablissements;
      }),
      tap((rows) => {
        this.etablissementsCache.set(stationId, { at: Date.now(), rows });
        this.etablissementsInflight.delete(stationId);
      }),
      catchError((err: HttpErrorResponse) => {
        this.etablissementsInflight.delete(stationId);
        return throwError(() => lcHttpError(err, 'Erreur lors du chargement des établissements.'));
      }),
      shareReplay(1)
    );
    this.etablissementsInflight.set(stationId, req$);
    return req$;
  }

  createEtablissement(body: EtablissementWriteBody): Observable<EtablissementRow> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/etablissement`;
    return this.http.post<{ success: boolean; etablissement?: EtablissementRow }>(url, body).pipe(
      map((res) => {
        if (!res?.success || !res.etablissement) {
          throw new Error('Création impossible');
        }
        this.invalidateEtablissementsCache();
        return res.etablissement;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la création de l\'établissement.'))
      )
    );
  }

  updateEtablissement(
    id: number,
    body: Partial<EtablissementWriteBody>
  ): Observable<EtablissementRow> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/etablissement/${id}`;
    return this.http.put<{ success: boolean; etablissement?: EtablissementRow }>(url, body).pipe(
      map((res) => {
        if (!res?.success || !res.etablissement) {
          throw new Error('Mise à jour impossible');
        }
        this.invalidateEtablissementsCache();
        return res.etablissement;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour de l\'établissement.'))
      )
    );
  }

  deleteEtablissement(id: number): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/etablissement/${id}`;
    return this.http.delete<{ success: boolean }>(url).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error('Suppression impossible');
        }
        this.invalidateEtablissementsCache();
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la suppression de l\'établissement.'))
      )
    );
  }

  setEtablissementEntrepots(etablissementId: number, entrepotIds: number[]): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/etablissement/${etablissementId}/entrepots`;
    return this.http.put<{ success: boolean }>(url, { entrepot_ids: entrepotIds }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error('Enregistrement impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de l\'association établissement / entrepôt.'))
      )
    );
  }

  listEtablissementEntrepotLinks(): Observable<EtablissementEntrepotLink[]> {
    const url = this.stationUrl('/etablissement-entrepots/station/:station_id');
    return this.http.get<{ success: boolean; links?: EtablissementEntrepotLink[] }>(url).pipe(
      map((res) => (res?.success && Array.isArray(res.links) ? res.links : [])),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des associations établissement.'))
      )
    );
  }
}
